import React, { useCallback, useState } from 'react';
import { CustomModal } from '@/shared/components/ui/modal';
import { SearchModalContent } from '@/shared/components/ui/Search';
import { useSearchPage } from '@/shared/hooks/useSearchPage';
import ActionLinkButton from '@/shared/components/ui/ActionLinkButton';
import { SearchIcon } from 'lucide-react';
import { usePageStore } from '@/features/pages/stores/usePageStore';
import { useCollectionStore } from '@/features/collections/stores/useCollectionStore';
import { useParams, useLocation } from 'react-router-dom';

export default function SearchButton({ showTitle = true }: { showTitle?: boolean }) {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  return (
    <>
      <ActionLinkButton
        Icon={SearchIcon}
        title={showTitle ? 'Search' : ''}
        onClick={() => {
          setIsModalOpen(true);
        }}
      />
      <SearchModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
      />
    </>
  );
}

export const SearchModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { collectionId } = useParams<{ collectionId: string }>();

  const pagesMetadata = usePageStore(state => state.pagesMetadata);
  const getCurrentCollectionPageMetadata = useCollectionStore(
    state => state.getCurrentCollectionPageMetadata
  );
  const currentCollectionPageMetadata = getCurrentCollectionPageMetadata();

  const isInCollection = location.pathname.includes('/collection/');
  const data = isInCollection ? currentCollectionPageMetadata : pagesMetadata;

  const searchPages = useCallback(
    (query: string) => {
      return data.filter(p => p.title.toLowerCase().includes(query.toLowerCase()));
    },
    [data.length]
  );

  const { searchQuery, searchResults, setSearchQuery, onClickPage } = useSearchPage({
    searchFn: searchPages,
    defaultResults: isInCollection ? currentCollectionPageMetadata : pagesMetadata,
    collectionId: isInCollection ? collectionId || '' : '',
  });

  const closeModal = () => {
    setSearchQuery('');
    onClose();
  };

  return (
    <CustomModal isOpen={isOpen} onClose={closeModal}>
      <SearchModalContent
        searchQuery={searchQuery}
        searchResults={searchResults}
        onSearchChange={setSearchQuery}
        onClickPage={onClickPage}
      />
    </CustomModal>
  );
};
